/** Shared helpers for SQUAD factsheet page-2 charts (mirrors client custom-portfolio widget). */

import { SQUAD_COLORS, SQUAD_FONT_STACK } from './templateConstants';
import { SQUAD_VECTOR_PIE } from './squadVectorDonut';

export const SQUAD_PORTFOLIO_TEXT_COLOR = SQUAD_COLORS.text;
export const SQUAD_PORTFOLIO_FONT_FAMILY = SQUAD_FONT_STACK;
/** Pie / donut labels — same Segoe UI stack as the rest of the factsheet. */
export const SQUAD_PORTFOLIO_PIE_FONT_FAMILY = SQUAD_FONT_STACK;
/** Pie slice % labels — client widget uses 14px; factsheet uses slightly smaller. */
export const SQUAD_PIE_SLICE_LABEL_FONT_SIZE_PX = 11;
/** Pie legend — aligned with factsheet bar chart labels (`SQUAD_TYPOGRAPHY.bar`). */
export const SQUAD_PIE_LEGEND_FONT_SIZE_PX = 9;
export const SQUAD_PIE_LEGEND_MARK_PX = 9;
/** Default factsheet pie canvas height. */
export const SQUAD_PIE_DEFAULT_HEIGHT_PX = 266;
/** Client pie canvas reference height — used to scale layout padding. */
export const SQUAD_PIE_CHART_REFERENCE_HEIGHT_PX = 328;

/** Portfolio table (Renditen) — mirrors client custom-portfolio table themeIndex 2. */
export const SQUAD_TABLE_LABEL_WIDTH_PCT = 70;
export const SQUAD_TABLE_VALUE_WIDTH_PCT = 30;
/** Factsheet scale — smaller than client widget 14px. */
export const SQUAD_TABLE_FONT_SIZE_PX = 9;
export const SQUAD_TABLE_FONT_SIZE_PDF_PT = 8;

/**
 * Rasterisation factor for the NAV chart on its way into the PDF.
 * At 3 the axis labels landed at 322 dpi and read visibly soft; 6 doubles that.
 * Purely a resolution knob — stroke width and label size are set in CSS px and
 * scale with the image, so the geometry on the page does not change.
 */
export const SQUAD_CHART_CAPTURE_SCALE = 6;

/**
 * NAV chart height in the PDF (pt) — this is what ends up on paper.
 *
 * SQUAD asked for a taller chart ("dass wir einfach möglichst hoch werden") and
 * explicitly accepted that the room comes out of the Monatskommentar below it.
 * 154 pt before.
 */
export const SQUAD_NAV_CHART_HEIGHT_PDF_PT = 182;

/** Main column width — preview px and PDF pt. Mirrors squadTitleFit. */
const PREVIEW_MAIN_WIDTH_PX = 794 - 22 * 2 - 180 - 12;
const PDF_MAIN_WIDTH_PT = 595.28 - 16 * 2 - 180 - 12;

/**
 * Height the chart is rendered at in the preview, before being captured and placed
 * in the PDF (px).
 *
 * Derived from the PDF height rather than chosen: the capture is placed at full
 * column width without preserving its aspect ratio, so any mismatch between the two
 * ratios stretches the image on one axis. It used to be rendered at 199 px and
 * placed at 154 pt, a 16 % vertical stretch — which is why the axis labels did not
 * sit at the 8 pt SQUAD asked for ("Beschriftung X & Y: gleiche Schriftgröße, wie
 * sonst überall") but at 8 pt wide by 9.3 pt tall. Keeping the ratios equal makes
 * the scale uniform and the labels land on the content base.
 */
export const SQUAD_NAV_CHART_HEIGHT_PX = Math.round(
  (SQUAD_NAV_CHART_HEIGHT_PDF_PT * PREVIEW_MAIN_WIDTH_PX) / PDF_MAIN_WIDTH_PT,
);

/**
 * Plot padding (px). ApexCharts reserves a generous gap above the topmost gridline
 * for an auto-scaled axis; with squadNavYRange fixing min/max there is nothing to
 * reserve, and the layout document marks that gap "Platz ausnutzen". The negative
 * top pulls the plot up into it — not all the way, because the topmost y-axis label
 * is centred on its gridline and would be cut in half at the image edge.
 */
export const SQUAD_NAV_CHART_GRID_PADDING = { top: -16, right: 6, bottom: 0, left: 2 } as const;

/** Pulls the % labels toward the page edge — "die Zahlen nach links, an die Kante". */
export const SQUAD_NAV_CHART_Y_LABEL_OFFSET_X = -6;

/**
 * Same treatment for the rolling performance bars ("auch hier die Abstände oben,
 * links und rechts minimieren"). Measured against the live options at 543 x 170 px:
 * the plot sat 31 px below the top edge, 40 px from the left and 49.5 px from the
 * right, leaving the bars only 86 px of the 170. With these values the frame comes
 * to 11 / 24 / 7.5 px and the bars get 106 px.
 *
 * The floor is the axis text, not the plot: the top % label is centred on its
 * gridline and keeps 5 px to the image edge, the % column keeps 2 px to the grid,
 * and the period labels stay far inside. `parentHeightOffset: 0` removes the 15 px
 * ApexCharts reserves above every chart for a title this one does not have.
 */
export const SQUAD_ROLLING_CHART_GRID_PADDING = {
  top: -20,
  right: -32,
  bottom: 0,
  left: -4,
} as const;

/** Moves the % column toward the page edge, as on the NAV chart. */
export const SQUAD_ROLLING_CHART_Y_LABEL_OFFSET_X = -8;

/**
 * NAV line width in CSS px. The SQUAD originals draw their curve at roughly 1.2 pt;
 * measured against the preview column this lands at 1.17 pt on the page.
 */
export const SQUAD_CHART_STROKE_PX = 1.75;
export const SQUAD_TABLE_BORDER_TOP = '#9d9d9c';
export const SQUAD_TABLE_BORDER_BOTTOM = '#d0d0d0';
/** Row rule thickness (client uses 2px; factsheet needs thinner). */
export const SQUAD_TABLE_BORDER_WIDTH_PX = 1;
export const SQUAD_TABLE_BORDER_WIDTH_PDF_PT = 0.75;
export const SQUAD_TABLE_TEXT_COLOR = SQUAD_COLORS.text;
export const SQUAD_TABLE_COLUMN_GAP_PX = 8;
export const SQUAD_TABLE_COLUMN_GAP_PDF_PT = 6;
/** Compact row padding for factsheet density. */
export const SQUAD_TABLE_ROW_PAD_Y_PX = 4;
export const SQUAD_TABLE_ROW_PAD_Y_PDF_PT = 3;
/** Client `PORTFOLIO_TABLE_COL1_SINGLE_LINE_TARGET_CHARS`. */
export const SQUAD_TABLE_COL1_SINGLE_LINE_MAX_CHARS = 37;

/**
 * Mindestabstand Banner → Torte (Seite 2 rechts). Ring und Legende stehen zentriert
 * nebeneinander; ist die Legende niedriger als der Ring, saß der Ring 4 pt (Vorschau 1 px)
 * unter dem Banner und wirkte angeklebt, bei höherer Legende weiter unten (Basistest
 * 02.10.2026: Value Bereich 3, Aguja Bereich 2). Balken haben durch ihre Zeilenhöhe Luft.
 */
export const SQUAD_PIE_TOP_GAP_PDF_PT = 6;
export const SQUAD_PIE_TOP_GAP_PX = 8;

/**
 * Luft zwischen Bereichsbalken und Inhalt (Seite 2 rechts), für Balken, Tabellen und Torten
 * gleich. Der Balken selbst hat nur 5 pt Abstand nach unten; seit die Legende oben bündig
 * steht (09.10.2026), klebte der Inhalt am Balken (Benjamin). SQUADs eigenes Factsheet lässt
 * deutlich mehr Luft (Grundlinie Überschrift → erste Zeile ≈ 25 pt gegen ≈ 13 pt hier).
 */
export const SQUAD_SECTION_CONTENT_GAP_PDF_PT = 5;
export const SQUAD_SECTION_CONTENT_GAP_PX = 7;

/**
 * Eigener Abstand der Torte im Bereich. Die Luft unter dem Balken hat sie schon, sonst stand
 * der Ring 11 statt 6 pt tiefer — zu viel (Benjamin, 09.10.2026). Ring bzw. obere Zahlen
 * bleiben damit insgesamt SQUAD_PIE_TOP_GAP unter dem Bereichsbalken, wie vorher.
 */
export const SQUAD_PIE_BLOCK_GAP_PDF_PT = Math.max(0, SQUAD_PIE_TOP_GAP_PDF_PT - SQUAD_SECTION_CONTENT_GAP_PDF_PT);
export const SQUAD_PIE_BLOCK_GAP_PX = Math.max(0, SQUAD_PIE_TOP_GAP_PX - SQUAD_SECTION_CONTENT_GAP_PX);

/**
 * Where the legend of a vector donut sits:
 * - 'top': at the top of the block, level with the bars or the table in the other column.
 * - 'ring': centred on the ring (09.10.2026 for pies alone or side by side). SQUAD asked on
 *   22.10.2026 for the legend at the top everywhere, so squadPieLegendAlign no longer
 *   returns it; the renderers still support it.
 */
export type SquadPieLegendAlign = 'ring' | 'top';

/**
 * 'top': the legend starts where the first table row's text does (row padding), whatever
 * gap the pie block keeps. Measured 09.10.2026 on SQUAD-TEST: table ≈ 1 pt, bars ≈ 2.5 pt
 * apart (PDF); before, a full line of numbers above the ring.
 */
export const SQUAD_PIE_LEGEND_TOP_OFFSET_PDF_PT = SQUAD_TABLE_ROW_PAD_Y_PDF_PT - SQUAD_PIE_BLOCK_GAP_PDF_PT;
export const SQUAD_PIE_LEGEND_TOP_OFFSET_PX = SQUAD_TABLE_ROW_PAD_Y_PX - SQUAD_PIE_BLOCK_GAP_PX;

/**
 * Neben Balken oder Tabelle ('top') rückt die Zeile mit den kleinen Zahlen über dem Ring in
 * die Luft unter dem Bereichsbalken — sonst begann sie auf Höhe der ersten Tabellenzeile und
 * der Ring hing darunter (Benjamin, 09.10.2026). Höchstens um die Höhe dieser Zeile: eine
 * Torte ohne Zahlen oben bleibt bündig mit Legende und Nachbarspalte. In pt; die Vorschau
 * rechnet mit ihrem px-Faktor um.
 */
export const SQUAD_PIE_LABEL_LIFT_PDF_PT = 8;
/**
 * Vorschau eigener Wert: dort hat der Bereichsbalken kaum Abstand nach unten; mit dem
 * umgerechneten PDF-Wert (≈ 11 px) stieß die oberste Zahl an den Balken. 5 px lassen ≈ 5 px Luft.
 */
export const SQUAD_PIE_LABEL_LIFT_PX = 5;

export function squadPieLabelLiftPt(legendAlign: SquadPieLegendAlign, topPt: number): number {
  return legendAlign === 'top' ? Math.min(Math.max(0, topPt), SQUAD_PIE_LABEL_LIFT_PDF_PT) : 0;
}

/** Vorschau: wie squadPieLabelLiftPt, in px (Zeilenhöhe bereits in px umgerechnet). */
export function squadPieLabelLiftPx(legendAlign: SquadPieLegendAlign, topPx: number): number {
  return legendAlign === 'top' ? Math.min(Math.max(0, topPx), SQUAD_PIE_LABEL_LIFT_PX) : 0;
}

/** SQUAD 22.10.2026 (Variante E1): legend — and with it the ring — at the top in every section. */
export function squadPieLegendAlign(): SquadPieLegendAlign {
  return 'top';
}

export const SQUAD_PIE_OUTSIDE_LABEL_THRESHOLD_PCT = 5;
export const SQUAD_PIE_INSIDE_LABEL_COLOR = '#ffffff';
export const SQUAD_PIE_INSIDE_LABEL_STROKE = '#000000';
export const SQUAD_PIE_INSIDE_LABEL_STROKE_WIDTH = 2;

/** Factsheet bar label wrap limit (widget uses 30; narrower two-column layout). */
export const SQUAD_BAR_LABEL_MAX_CHARS = 30;
export const SQUAD_BAR_LABEL_COLUMN_FALLBACK_PX = 118;
export const SQUAD_BAR_LABEL_BAR_GAP_PX = 8;
export const SQUAD_BAR_RIGHT_MARGIN_PX = 12;
export const SQUAD_BAR_HEIGHT_PX = 14;
export const SQUAD_VALUE_INSIDE_MIN_BAR_PCT = 20;

/** react-pdf bar chart — fixed pt sizes for narrow two-column page-2 layout */
export const SQUAD_BAR_LABEL_COLUMN_PDF_PT = 96;
export const SQUAD_BAR_LABEL_BAR_GAP_PDF_PT = 6;
export const SQUAD_BAR_HEIGHT_PDF_PT = 11;
export const SQUAD_BAR_ROW_MARGIN_PDF_PT = 3;
/** Single-line row height — label lineHeight matches bar height for vertical alignment */
export const SQUAD_BAR_ROW_HEIGHT_PDF_PT = 12;

const hexToRgb = (hex: string) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
};

const rgbToHex = (r: number, g: number, b: number): string =>
  `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;

export function isLightColor(hex: string): boolean {
  const rgb = hexToRgb(hex);
  if (!rgb) return false;
  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return luminance > 0.62;
}

/** Gradient palette from primary — slice 0 is exact primary, then progressively lighter. */
export function generateGradientColors(mainColor: string, count: number): string[] {
  if (count <= 0) return [];
  const mainRgb = hexToRgb(mainColor);
  if (!mainRgb) return Array(count).fill(mainColor);
  if (count === 1) return [mainColor];

  const colors: string[] = [mainColor];
  for (let i = 1; i < count; i++) {
    const factor = i / (count - 1);
    const newRgb = {
      r: Math.round(mainRgb.r + (255 - mainRgb.r) * factor * 0.85),
      g: Math.round(mainRgb.g + (255 - mainRgb.g) * factor * 0.85),
      b: Math.round(mainRgb.b + (255 - mainRgb.b) * factor * 0.85),
    };
    colors.push(rgbToHex(newRgb.r, newRgb.g, newRgb.b));
  }
  return colors;
}

export function formatPortfolioPercent(value: number, decimals = 2): string {
  if (!Number.isFinite(value)) return '0%';
  return `${value.toFixed(decimals).replace('.', ',')}%`;
}

/**
 * Torten-Prozentwerte ohne Nachkommastellen (Weekly 02.10.2026) — Legende, Bild-Torte und
 * Ring-Zahlen runden gleich: kaufmännisch, Werte über 0 unter 0,5 als „<1%“.
 */
export function formatSquadPiePercent(value: number): string {
  if (!Number.isFinite(value)) return '0%';
  if (value > 0 && value < 0.5) return '<1%';
  return `${Math.round(value) || 0}%`;
}

/**
 * Width of a legend's value column: 18 pt fits „99%“ and „<1%“; a four-character value
 * („100%“, or bad data like „150%“/„-10%“) gets 22 pt so react-pdf does not break it.
 */
export function squadPieValueColumnPt(values: number[]): number {
  return values.some((v) => formatSquadPiePercent(v).length >= 4)
    ? SQUAD_VECTOR_PIE.valueColumnWidePt
    : SQUAD_VECTOR_PIE.valueColumnPt;
}

export function getSliceSharePercent(value: number, total: number): number {
  if (total <= 0 || !Number.isFinite(value)) return 0;
  return (Math.max(value, 0) / total) * 100;
}

export function isPieOutsideLabel(value: number, total: number): boolean {
  const pct = getSliceSharePercent(value, total);
  return pct > 0 && pct < SQUAD_PIE_OUTSIDE_LABEL_THRESHOLD_PCT;
}

/** On-chart slice label — below 5% share: integer only; otherwise integer with %. */
export function formatPieSliceDataLabel(value: number, total: number): string {
  if (!Number.isFinite(value)) return '';
  const sharePct = getSliceSharePercent(value, total);
  if (sharePct > 0 && sharePct < SQUAD_PIE_OUTSIDE_LABEL_THRESHOLD_PCT) {
    return String(Math.round(value));
  }
  return formatSquadPiePercent(value);
}

export function squadPieLayoutPadding(canvasHeight: number) {
  const scale = canvasHeight / SQUAD_PIE_CHART_REFERENCE_HEIGHT_PX;
  // Tight padding — maximizes donut radius in the narrow factsheet column.
  return {
    top: Math.round(16 * scale),
    right: Math.round(8 * scale),
    bottom: Math.round(16 * scale),
    left: Math.round(8 * scale),
  };
}

function wrapLabel(label: string, maxChars: number): string[] {
  const text = String(label ?? '').trim();
  if (!text) return [''];
  if (text.length <= maxChars) return [text];

  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let current = '';

  const flush = () => {
    if (current) {
      lines.push(current);
      current = '';
    }
  };

  for (const word of words) {
    if (!current) {
      current = word.length <= maxChars ? word : `${word.slice(0, maxChars)}-`;
      if (word.length > maxChars) lines.push(current), (current = word.slice(maxChars));
      continue;
    }
    const next = `${current} ${word}`;
    if (next.length <= maxChars) {
      current = next;
    } else {
      flush();
      current = word.length <= maxChars ? word : `${word.slice(0, maxChars)}-`;
      if (word.length > maxChars) lines.push(current), (current = word.slice(maxChars));
    }
  }
  flush();
  return lines.length ? lines : [''];
}

export function squadBarChartLabelText(label: string): string {
  return wrapLabel(label.replace(/\r\n?/g, '\n').split('\n').join(' '), SQUAD_BAR_LABEL_MAX_CHARS).join('\n');
}

/** Client portfolio table col-1 wrap: nowrap ≤37 chars (no Alt+Enter), else break-word / pre-line. */
export function squadTableLabelWhiteSpace(label: string): 'nowrap' | 'pre-line' | 'normal' {
  const text = String(label ?? '')
    .replace(/_x000D_/gi, '\n')
    .replace(/\r\n?/g, '\n')
    .trim();
  if (!text) return 'normal';
  if (/\n/.test(text)) return 'pre-line';
  if (text.length <= SQUAD_TABLE_COL1_SINGLE_LINE_MAX_CHARS) return 'nowrap';
  return 'normal';
}

export function shouldShowBarValueInside(barPct: number, formattedValue: string): boolean {
  if (barPct < SQUAD_VALUE_INSIDE_MIN_BAR_PCT) return false;
  const minPctForText = (formattedValue.length + 2) * 3;
  return barPct >= Math.max(SQUAD_VALUE_INSIDE_MIN_BAR_PCT + 4, minPctForText);
}
